import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Check, Trash2, Loader2, AlertCircle } from 'lucide-react'
import { alertsApi } from '../lib/api'
import { cn } from '../lib/utils'

interface Alert {
  id: string
  type: string
  title: string
  message: string
  isRead: boolean
  createdAt: string
}

export function Alerts() {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', filter],
    queryFn: () => alertsApi.getAll({ unreadOnly: filter === 'unread' }),
  })

  const markReadMutation = useMutation({
    mutationFn: (id: string) => alertsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['alertCount'] })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => alertsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['alertCount'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['alertCount'] })
    },
  })

  const alerts: Alert[] = data?.data?.alerts || []
  const unreadCount = data?.data?.unreadCount || 0

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'TRADE_EXECUTED':
        return <div className="w-2 h-2 rounded-full bg-green-500" />
      case 'OPPORTUNITY':
        return <div className="w-2 h-2 rounded-full bg-blue-500" />
      case 'RISK_WARNING':
        return <div className="w-2 h-2 rounded-full bg-yellow-500" />
      case 'SYSTEM':
        return <div className="w-2 h-2 rounded-full bg-gray-500" />
      case 'WALLET_ACTIVITY':
        return <div className="w-2 h-2 rounded-full bg-purple-500" />
      default:
        return <div className="w-2 h-2 rounded-full bg-gray-400" />
    }
  }

  const getAlertBg = (type: string, isRead: boolean) => {
    if (isRead) return 'bg-white'
    switch (type) {
      case 'TRADE_EXECUTED':
        return 'bg-green-50'
      case 'OPPORTUNITY':
        return 'bg-blue-50'
      case 'RISK_WARNING':
        return 'bg-yellow-50'
      default:
        return 'bg-gray-50'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
          <p className="text-gray-600">
            {unreadCount > 0 ? (
              <span className="text-blue-600 font-medium">{unreadCount} unread</span>
            ) : (
              'No new alerts'
            )}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setFilter('all')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                filter === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                filter === 'unread'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              Unread
            </button>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Check className="w-4 h-4 mr-2" />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Alerts List */}
      {alerts.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No alerts</h3>
          <p className="text-gray-500">
            {filter === 'unread'
              ? "You've read all your alerts!"
              : 'Alerts will appear here when there are new opportunities, trades, or important updates.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={cn(
                'rounded-xl border border-gray-200 p-4 transition-all hover:shadow-md',
                getAlertBg(alert.type, alert.isRead)
              )}
            >
              <div className="flex items-start space-x-4">
                <div className="mt-1">{getAlertIcon(alert.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={cn(
                      'text-sm font-medium',
                      alert.isRead ? 'text-gray-700' : 'text-gray-900'
                    )}>
                      {alert.title}
                    </h3>
                    <span className="text-xs text-gray-500">
                      {new Date(alert.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className={cn(
                    'text-sm',
                    alert.isRead ? 'text-gray-500' : 'text-gray-700'
                  )}>
                    {alert.message}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {!alert.isRead && (
                    <button
                      onClick={() => markReadMutation.mutate(alert.id)}
                      disabled={markReadMutation.isPending}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteMutation.mutate(alert.id)}
                    disabled={deleteMutation.isPending}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination info */}
      {alerts.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <p>
            Showing {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
          </p>
          {data?.data?.pagination && (
            <p>
              Page {data.data.pagination.page} of {data.data.pagination.pages}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
